/**
 * Pure helpers for parsing the EVSE SSE event stream.
 *
 * Kept in a separate module from `useSmartAmpSse` so they can be unit-tested
 * (and imported by the Redux slice) without pulling in the React/Redux
 * runtime dependencies of the hook.
 */

import { CloudFirmwareStatus, FirmwareUpdateStatus } from '../ev-charger/types';

export type SmartAmpStatus = 'inactive' | 'active' | 'chargingLimited';

export type SmartAmpSseState = {
  loadActive: boolean;
  tempActive: boolean;
  status: SmartAmpStatus;
};

/**
 * The SSE stream carries four event types (identified by the `event_type`
 * JSON field, which maps to the `messageType` header in the AsyncAPI spec):
 *
 *   - `ChargerStatusUpdate` — charger status change; uses
 *     `ChargerStatusUpdateMessage` schema (has `chargerId`, `chargerSendingDate`, `status`,
 *     `error`, `vendorErrorCode`, `info`, `control_mode`, `is_scheduled_charge_active`,
 *     `scheduled_charge_start_time`, `scheduled_charge_end_time`)
 *   - `StopTransaction` — end of a charging transaction; uses
 *     `ChargerTransactionEventMessage` schema (has `chargerId`, `chargerSendingDate`,
 *     `status`, `error`, `transactionStartDate`, `transactionEndDate`, `kwhConsumed`,
 *     `amountUsage`, `currency`, `kwInstantaneousPower`; NO `info`)
 *   - `MeterValues` — periodic energy/cost update; uses
 *     `ChargerTransactionEventMessage` schema (same fields as `StopTransaction`)
 *   - `CloudStatusUpdate` — cloud status change; includes `control_mode`
 *      but no SmartAmp `info` payload
 *
 * Only `ChargerStatusUpdate` carries the `info` field with SmartAmp derating
 * flags (`DeratingSUA`, `DeratingThermal`). `StartTransaction` is NOT
 * forwarded via the stream.
 */

export type ChargerStatusUpdateEvent = {
  chargerId: string;
  chargerSendingDate: string;
  status: string;
  error: string;
  vendorErrorCode?: string | null;
  info: string | null;
  control_mode?: 'Manual' | 'Automatic' | null;
  is_scheduled_charge_active?: boolean | null;
  scheduled_charge_start_time?: string | null;
  scheduled_charge_end_time?: string | null;
  event_type: 'ChargerStatusUpdate';
};

/**
 * `StopTransaction` and `MeterValues` share the `ChargerTransactionEventMessage`
 * schema from the AsyncAPI spec.
 */
export type ChargerTransactionEvent = {
  chargerId: string;
  chargerSendingDate: string;
  transactionStartDate: string | null;
  transactionEndDate: string | null;
  kwhConsumed: number;
  amountUsage: number | null;
  currency: string | null;
  kwInstantaneousPower: number;
  maxAmps?: number | null;
  chargingAmps?: number | null;
  status: string;
  error: string;
  vendorErrorCode?: string | null;
  event_type: 'StopTransaction' | 'MeterValues';
};

export type CloudStatusUpdateEvent = {
  chargerId: string;
  cloudStatusSendingDate: string;
  cloudStatus: string;
  cloudError: string;
  control_mode: 'Manual' | 'Automatic' | null;
  event_type: 'CloudStatusUpdate';
};

/**
 * `FirmwareUpdateStatus` - firmware update lifecycle event emitted by the
 * Clipsal Cloud after the eMobility hub triggers the initial firmware update.
 *
 * `firmwareStatus` is the granular OCPP-level step; `cloudFirmwareStatus` is
 * the high-level aggregate used to drive UI state.
 */
export type FirmwareUpdateStatusEvent = {
  chargerId: string;
  chargerSendingDate: string;
  firmwareStatus?: FirmwareUpdateStatus;
  cloudFirmwareStatus: CloudFirmwareStatus;
  initialFirmwareVersion: string;
  firmwareVersion: string;
  firmwareUpdateBeginDate: string;
  event_type: 'FirmwareUpdateStatus';
};

/**
 * Parses the `info` field from a `ChargerStatusUpdate` event.
 *
 * The field is a flat key=value string with entries separated by semicolons
 * OR commas (both formats have been observed in the wild):
 *   - `"DeratingSUA=1;DeratingThermal=0"`      ← semicolons, no spaces
 *   - `"DeratingSUA = 1, DeratingThermal = 0"` ← commas, spaces
 *
 * The parser handles both (and mixed) separators robustly.
 */
export function parseInfo(info: string | null): { loadActive: boolean; tempActive: boolean } {
  if (!info) return { loadActive: false, tempActive: false };
  const fields: Record<string, string> = {};
  // Split on semicolons or commas — covers every observed firmware format.
  info.split(/[;,]/).forEach((part) => {
    const [key, val] = part.split('=');
    if (key) fields[key.trim()] = (val ?? '').trim();
  });
  return {
    loadActive: fields['DeratingSUA'] === '1',
    tempActive: fields['DeratingThermal'] === '1',
  };
}

export function deriveStateFromEvent(event: ChargerStatusUpdateEvent): SmartAmpSseState {
  const { loadActive, tempActive } = parseInfo(event.info);
  const anyActive = loadActive || tempActive;
  const isCharging = event.status === 'Charging';

  let status: SmartAmpStatus = 'inactive';
  if (anyActive) {
    status = isCharging ? 'active' : 'chargingLimited';
  }

  return { loadActive, tempActive, status };
}

export function parseChargerStatusEvent(line: string): ChargerStatusUpdateEvent | null {
  try {
    const obj = JSON.parse(line) as ChargerStatusUpdateEvent;
    if (obj.event_type === 'ChargerStatusUpdate') return obj;
  } catch {
    // ignore malformed or non-JSON lines
  }
  return null;
}

export function parseTransactionEvent(line: string): ChargerTransactionEvent | null {
  try {
    const obj = JSON.parse(line) as ChargerTransactionEvent;
    if (obj.event_type === 'StopTransaction' || obj.event_type === 'MeterValues') return obj;
  } catch {
    // ignore malformed or non-JSON lines
  }
  return null;
}

export function parseCloudStatusEvent(line: string): CloudStatusUpdateEvent | null {
  try {
    const obj = JSON.parse(line) as CloudStatusUpdateEvent;
    if (obj.event_type === 'CloudStatusUpdate') return obj;
  } catch {
    // ignore malformed or non-JSON lines
  }
  return null;
}

export function parseFirmwareUpdateStatusEvent(line: string): FirmwareUpdateStatusEvent | null {
  try {
    const obj = JSON.parse(line) as FirmwareUpdateStatusEvent;
    if (obj.event_type === 'FirmwareUpdateStatus') return obj;
  } catch {
    // ignore malformed or non-JSON lines
  }
  return null;
}
